import type { SpoilageItemRow } from "@/api/spoilage.tsx"

export type SpoilageView = "item" | "warehouse"

/** A row of the table: one item, at one warehouse or across all of them. */
export interface SpoilageDisplayRow {
  key: string
  source: string
  dc: string | null
  upc: string | null
  item_number: string | null
  description: string | null
  amount: number
  units: number | null
  deduction_count: number
  last_invoice_date: string
  /** Share of the rows shown, 0–1. */
  share: number
}

function to_cents(amount: number): number {
  return Math.round(amount * 100)
}

/**
 * The rows to show for one distributor: product written off or allowances, by item across every
 * warehouse or split by warehouse, largest first.
 *
 * `deduction_count` is summed across warehouses, which is exact here: a spoilage document is
 * issued by one warehouse.
 */
export function spoilage_rows(items: SpoilageItemRow[], source: string, allowance: boolean, view: SpoilageView): SpoilageDisplayRow[] {
  const grouped = new Map<string, SpoilageDisplayRow & { cents: number }>()
  for (const item of items) {
    if (item.source !== source || item.allowance !== allowance) {
      continue
    }
    const key = view === "warehouse" ? `${item.dc ?? ""}|${item.item_key}` : item.item_key
    const existing = grouped.get(key)
    if (!existing) {
      grouped.set(key, {
        key,
        source: item.source,
        dc: view === "warehouse" ? item.dc : null,
        upc: item.upc,
        item_number: item.item_number,
        description: item.description,
        amount: 0,
        cents: to_cents(item.amount),
        units: item.units,
        deduction_count: item.deduction_count,
        last_invoice_date: item.last_invoice_date,
        share: 0,
      })
      continue
    }
    existing.cents += to_cents(item.amount)
    if (item.units !== null) {
      existing.units = (existing.units ?? 0) + item.units
    }
    existing.deduction_count += item.deduction_count
    // The most recent document names the item.
    if (item.last_invoice_date > existing.last_invoice_date) {
      existing.last_invoice_date = item.last_invoice_date
      existing.upc = item.upc ?? existing.upc
      existing.item_number = item.item_number ?? existing.item_number
      existing.description = item.description ?? existing.description
    }
  }
  const rows = [...grouped.values()]
  const total_cents = rows.reduce((sum: number, row) => sum + row.cents, 0)
  return rows
    .map(({ cents, ...row }) => ({ ...row, amount: cents / 100, share: total_cents > 0 ? cents / total_cents : 0 }))
    .sort((a: SpoilageDisplayRow, b: SpoilageDisplayRow) => b.amount - a.amount || a.key.localeCompare(b.key))
}

/** Dollars across rows, summed in cents so a long list does not drift. */
export function total_amount(rows: { amount: number }[]): number {
  return rows.reduce((sum: number, row) => sum + to_cents(row.amount), 0) / 100
}

function csv_cell(value: string | number | null): string {
  const text = value === null ? "" : String(value)
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/** The table as a CSV a brand can hand to a distributor's buyer. */
export function spoilage_csv(rows: SpoilageDisplayRow[], distributor: string, view: SpoilageView): string {
  const header = ["Distributor", ...(view === "warehouse" ? ["Warehouse"] : []), "Item", "UPC", "Distributor item #", "Amount", "Units", "Deductions", "Last charged"]
  const lines = rows.map((row: SpoilageDisplayRow) =>
    [
      distributor,
      ...(view === "warehouse" ? [row.dc] : []),
      row.description,
      row.upc,
      row.item_number,
      row.amount.toFixed(2),
      row.units,
      row.deduction_count,
      row.last_invoice_date,
    ]
      .map(csv_cell)
      .join(",")
  )
  return [header.join(","), ...lines].join("\n")
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  function item(overrides: Partial<SpoilageItemRow>): SpoilageItemRow {
    return {
      source: "kehe",
      allowance: false,
      dc: "19 DFW",
      item_key: "upc:86872800030",
      upc: "86872800030",
      item_number: "02412647",
      description: "TOFU 5 SPICE SMOKED ORG",
      amount: 100,
      units: 30,
      deduction_count: 1,
      last_invoice_date: "2025-10-14",
      ...overrides,
    }
  }

  describe("spoilage_rows", () => {
    const items = [
      item({ amount: 1706.94, units: 450, deduction_count: 2 }),
      item({ dc: "55 DGV", amount: 1396.99, units: 384, last_invoice_date: "2025-12-17" }),
      item({ item_key: "upc:86872800036", upc: "86872800036", description: "TOFU NOODLES", amount: 0.1, units: 1 }),
      item({ item_key: "upc:86872800036", upc: "86872800036", description: "TOFU NOODLES", dc: "55 DGV", amount: 0.2, units: 2 }),
      item({ allowance: true, amount: 5, units: null }),
      item({ source: "unfi", amount: 9 }),
    ]

    it("adds one item up across warehouses", () => {
      const rows = spoilage_rows(items, "kehe", false, "item")
      expect(rows.map((r) => [r.description, r.amount, r.units, r.deduction_count, r.dc])).toEqual([
        ["TOFU 5 SPICE SMOKED ORG", 3103.93, 834, 3, null],
        ["TOFU NOODLES", 0.3, 3, 2, null],
      ])
      expect(rows[0].last_invoice_date).toBe("2025-12-17")
      expect(rows[0].share + rows[1].share).toBeCloseTo(1)
    })

    it("keeps warehouses apart when asked", () => {
      const rows = spoilage_rows(items, "kehe", false, "warehouse")
      expect(rows.map((r) => [r.dc, r.amount])).toEqual([
        ["19 DFW", 1706.94],
        ["55 DGV", 1396.99],
        ["55 DGV", 0.2],
        ["19 DFW", 0.1],
      ])
    })

    it("shows allowances and other distributors only when asked for", () => {
      expect(spoilage_rows(items, "kehe", true, "item").map((r) => [r.amount, r.units])).toEqual([[5, null]])
      expect(spoilage_rows(items, "unfi", false, "item").map((r) => r.amount)).toEqual([9])
      expect(spoilage_rows(items, "ahold", false, "item")).toEqual([])
    })

    it("sums in cents", () => {
      expect(total_amount([{ amount: 0.1 }, { amount: 0.2 }])).toBe(0.3)
    })
  })

  describe("spoilage_csv", () => {
    it("quotes a description that carries a comma", () => {
      const rows = spoilage_rows([item({ source: "unfi", dc: "MAN", upc: null, item_number: "305161", description: "SOY MILK,OG2 SWEETENED", amount: 115.26, units: 6 })], "unfi", false, "warehouse")
      expect(spoilage_csv(rows, "UNFI", "warehouse")).toBe(
        'Distributor,Warehouse,Item,UPC,Distributor item #,Amount,Units,Deductions,Last charged\nUNFI,MAN,"SOY MILK,OG2 SWEETENED",,305161,115.26,6,1,2025-10-14'
      )
    })
  })
}
